import type { AnyAction } from 'redux';
import { v4 as uuidv4 } from 'uuid';

import { deepCopy } from '../../../../../shared/utils';
import {
  CLEAR_QUICK_LINK,
  CREATE_QUICK_LINK,
  FETCH_QUICK_LINK_COLLECTIONS,
  FETCH_QUICK_LINK_TEMPLATE,
  SEARCH_ANDROID_APP_NAME,
  SEARCH_IOS_APP_NAME,
  UPDATE_ALIAS,
  UPDATE_ANDROID,
  UPDATE_ANDROID_REDIRECT,
  UPDATE_CAMPAIGN,
  UPDATE_CHANNEL,
  UPDATE_DESKTOP,
  UPDATE_DESKTOP_REDIRECT,
  UPDATE_EDITED_QUICK_LINK,
  UPDATE_FEATURE,
  UPDATE_IOS,
  UPDATE_IOS_REDIRECT,
  UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
  UPDATE_IS_BLACKLIST_ERROR,
  UPDATE_IS_EDITING,
  UPDATE_IS_GENERAL_STEP_VISITED,
  UPDATE_IS_LINK_DATA_STEP_VISITED,
  UPDATE_IS_REDIRECTS_STEP_VISITED,
  UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
  UPDATE_LINK_DATA,
  UPDATE_ORIGINAL_WEB_URL,
  UPDATE_QR_CODE_SETTINGS,
  UPDATE_SELECTED_QUICK_LINK,
  UPDATE_SELECTED_TEMPLATE,
  UPDATE_SOCIAL_DESCRIPTION,
  UPDATE_SOCIAL_IMAGE,
  UPDATE_SOCIAL_TITLE,
  UPDATE_TAGS,
  UPDATE_TITLE,
  UPDATE_WEB_ONLY_LINK,
} from '../../actions/QuickLinkCreator/QuickLinkCreatorActions';
import { reducerWrapper } from '../../support/utils';
import type { QuickLinkCreatorReducerType } from './types/QuickLinkCreatorReducerType';

export const INIT_STATE: QuickLinkCreatorReducerType = {
  isGeneralStepVisited: false,
  isAnalyticsTagsStepVisited: false,
  isRedirectsStepVisited: false,
  isLinkDataStepVisited: false,
  isSocialMediaStepVisited: false,
  selectedTemplate: undefined,
  title: undefined,
  quickLinkCollections: undefined,
  alias: undefined,
  originalWebUrl: undefined,
  feature: undefined,
  channel: undefined,
  campaign: undefined,
  tags: [],
  iosRedirect: 'default',
  androidRedirect: 'default',
  desktopRedirect: 'default',
  ios: undefined,
  android: undefined,
  desktop: undefined,
  webOnlyLink: false,
  iosAppName: undefined,
  androidAppName: undefined,
  linkData: [{ id: uuidv4(), key: undefined, value: undefined }],
  isBlacklistError: false,
  socialTitle: undefined,
  socialDescription: undefined,
  socialImage: undefined,
  createdQuickLink: undefined,
  selectedQuickLink: undefined,
  isEditing: false,
  editedQuickLink: undefined,
};

const Handler = (state: QuickLinkCreatorReducerType, action: AnyAction): QuickLinkCreatorReducerType => {
  switch (action.type) {
    case CLEAR_QUICK_LINK.response:
      return { ...deepCopy(INIT_STATE) };
    case UPDATE_IS_GENERAL_STEP_VISITED.response:
      return {
        ...state,
        isGeneralStepVisited: action.data?.isGeneralStepVisited,
      };
    case UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED.response:
      return {
        ...state,
        isAnalyticsTagsStepVisited: action.data?.isAnalyticsTagsStepVisited,
      };
    case UPDATE_IS_REDIRECTS_STEP_VISITED.response:
      return {
        ...state,
        isRedirectsStepVisited: action.data?.isRedirectsStepVisited,
      };
    case UPDATE_IS_LINK_DATA_STEP_VISITED.response:
      return {
        ...state,
        isLinkDataStepVisited: action.data?.isLinkDataStepVisited,
      };
    case UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED.response:
      return {
        ...state,
        isSocialMediaStepVisited: action.data?.isSocialMediaStepVisited,
      };
    case UPDATE_SELECTED_TEMPLATE.response:
      if (!action.data?.selectedTemplate) {
        return {
          ...INIT_STATE,
          alias: state.alias,
          isEditing: state.isEditing,
          title: state.title,
        };
      }

      return {
        ...state,
        selectedTemplate: action.data?.selectedTemplate,
      };
    case UPDATE_TITLE.response:
      return {
        ...state,
        title: action.data?.title,
      };
    case FETCH_QUICK_LINK_COLLECTIONS.response:
      return {
        ...state,
        quickLinkCollections: action.data?.quickLinkCollections,
      };
    case UPDATE_ALIAS.response:
      return {
        ...state,
        alias: action.data?.alias,
      };
    case UPDATE_ORIGINAL_WEB_URL.response: {
      const { originalWebUrl } = action.data;
      const newRedirects: Record<string, string> = {};

      const platformsToUpdate = state.webOnlyLink ? ['ios', 'android', 'desktop'] : ['desktop'];

      for (const platform of platformsToUpdate) {
        const redirectRules = state.selectedTemplate?.template?.redirects?.[platform];
        const allowsWebRedirect = !redirectRules?.redirectType || redirectRules.redirectType.toLowerCase() === 'any';

        if (allowsWebRedirect) {
          newRedirects[`${platform}Redirect`] = 'web';
          newRedirects[platform] = originalWebUrl;
        }
      }

      return {
        ...state,
        originalWebUrl,
        ...newRedirects,
      };
    }
    case UPDATE_FEATURE.response:
      return {
        ...state,
        feature: action.data?.feature,
      };
    case UPDATE_CHANNEL.response:
      return {
        ...state,
        channel: action.data?.channel,
      };
    case UPDATE_CAMPAIGN.response:
      return {
        ...state,
        campaign: action.data?.campaign,
      };
    case UPDATE_TAGS.response:
      return {
        ...state,
        tags: action.data?.tags,
      };
    case UPDATE_IOS_REDIRECT.response:
      return {
        ...state,
        iosRedirect: action.data?.iosRedirect,
      };
    case UPDATE_ANDROID_REDIRECT.response:
      return {
        ...state,
        androidRedirect: action.data?.androidRedirect,
      };
    case UPDATE_DESKTOP_REDIRECT.response:
      return {
        ...state,
        desktopRedirect: action.data?.desktopRedirect,
      };
    case UPDATE_IOS.response:
      return {
        ...state,
        ios: action.data?.ios,
      };
    case UPDATE_ANDROID.response:
      return {
        ...state,
        android: action.data?.android,
      };
    case UPDATE_DESKTOP.response:
      return {
        ...state,
        desktop: action.data?.desktop,
      };
    case UPDATE_WEB_ONLY_LINK.response:
      const { webOnlyLink } = action.data;

      const isSwitchingFromAppToWeb = webOnlyLink && !state.webOnlyLink && state.originalWebUrl;

      if (isSwitchingFromAppToWeb) {
        const newRedirects = {};

        const platformsToUpdate = ['ios', 'android', 'desktop'];

        for (const platform of platformsToUpdate) {
          const redirectRules = state.selectedTemplate?.template?.redirects?.[platform];
          const allowsWebRedirect = !redirectRules?.redirectType || redirectRules.redirectType.toLowerCase() === 'any';

          const currentRedirectType = state[`${platform}Redirect`];
          const currentUrl = state[platform];

          if (allowsWebRedirect && (currentRedirectType !== 'web' || !currentUrl)) {
            newRedirects[`${platform}Redirect`] = 'web';
            newRedirects[platform] = state.originalWebUrl;
          }
        }

        return {
          ...state,
          webOnlyLink,
          ...newRedirects,
          linkData: state.linkData.filter((item) => item.key?.value !== '$web_only'),
        };
      }

      return {
        ...state,
        webOnlyLink,
      };
    case SEARCH_IOS_APP_NAME.response:
      return {
        ...state,
        iosAppName: action.data?.iosAppName,
      };
    case SEARCH_ANDROID_APP_NAME.response:
      return {
        ...state,
        androidAppName: action.data?.androidAppName,
      };
    case UPDATE_LINK_DATA.response:
      return {
        ...state,
        linkData: action.data?.linkData,
      };
    case UPDATE_IS_BLACKLIST_ERROR.response:
      return {
        ...state,
        isBlacklistError: action.data?.isBlacklistError,
      };
    case UPDATE_SOCIAL_TITLE.response:
      return {
        ...state,
        socialTitle: action.data?.socialTitle,
      };
    case UPDATE_SOCIAL_DESCRIPTION.response:
      return {
        ...state,
        socialDescription: action.data?.socialDescription,
      };
    case UPDATE_SOCIAL_IMAGE.response:
      return {
        ...state,
        socialImage: action.data?.socialImage,
      };
    case UPDATE_QR_CODE_SETTINGS.response:
      return {
        ...state,
        qrCodeSettings: action.data?.qrCodeSettings,
      };
    case CREATE_QUICK_LINK.response:
      return {
        ...state,
        createdQuickLink: action.data?.createdQuickLink,
      };
    case UPDATE_SELECTED_QUICK_LINK.response:
      return {
        ...state,
        selectedQuickLink: action.data?.selectedQuickLink,
      };
    case UPDATE_IS_EDITING.response:
      return {
        ...state,
        isEditing: action.data?.isEditing,
      };
    case UPDATE_EDITED_QUICK_LINK.response:
      return {
        ...state,
        editedQuickLink: action.data?.editedQuickLink,
      };
    default:
      return state;
  }
};

const TypelessHandler = (state: any, action: any) => {
  return Handler(state, action);
};

const QuickLinkCreatorReducer = reducerWrapper(INIT_STATE, TypelessHandler, {
  request: [
    CLEAR_QUICK_LINK,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
    UPDATE_SELECTED_TEMPLATE,
    FETCH_QUICK_LINK_TEMPLATE,
    FETCH_QUICK_LINK_COLLECTIONS,
    UPDATE_TITLE,
    UPDATE_ALIAS,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_FEATURE,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    UPDATE_WEB_ONLY_LINK,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_SOCIAL_TITLE,
    UPDATE_SOCIAL_DESCRIPTION,
    UPDATE_SOCIAL_IMAGE,
    UPDATE_QR_CODE_SETTINGS,
    CREATE_QUICK_LINK,
    UPDATE_SELECTED_QUICK_LINK,
    UPDATE_IS_EDITING,
    UPDATE_EDITED_QUICK_LINK,
  ],
  error: [
    CLEAR_QUICK_LINK,
    UPDATE_IS_GENERAL_STEP_VISITED,
    UPDATE_IS_ANALYTICS_TAGS_STEP_VISITED,
    UPDATE_IS_LINK_DATA_STEP_VISITED,
    UPDATE_IS_REDIRECTS_STEP_VISITED,
    UPDATE_IS_SOCIAL_MEDIA_STEP_VISITED,
    UPDATE_SELECTED_TEMPLATE,
    FETCH_QUICK_LINK_TEMPLATE,
    FETCH_QUICK_LINK_COLLECTIONS,
    UPDATE_TITLE,
    UPDATE_ALIAS,
    UPDATE_ORIGINAL_WEB_URL,
    UPDATE_FEATURE,
    UPDATE_CHANNEL,
    UPDATE_CAMPAIGN,
    UPDATE_IOS_REDIRECT,
    UPDATE_ANDROID_REDIRECT,
    UPDATE_DESKTOP_REDIRECT,
    UPDATE_IOS,
    UPDATE_ANDROID,
    UPDATE_DESKTOP,
    UPDATE_WEB_ONLY_LINK,
    SEARCH_IOS_APP_NAME,
    SEARCH_ANDROID_APP_NAME,
    UPDATE_LINK_DATA,
    UPDATE_IS_BLACKLIST_ERROR,
    UPDATE_SOCIAL_TITLE,
    UPDATE_SOCIAL_DESCRIPTION,
    UPDATE_SOCIAL_IMAGE,
    UPDATE_QR_CODE_SETTINGS,
    CREATE_QUICK_LINK,
    UPDATE_SELECTED_QUICK_LINK,
    UPDATE_IS_EDITING,
    UPDATE_EDITED_QUICK_LINK,
  ],
});

export default QuickLinkCreatorReducer;
